// Port of lib/rectification.py — the matching/refinement orchestration layer.
//
// ADAPTATIONS versus Python:
//  - The OpenAI call (process_user_answers) is async in the TS port, so every function on that
//    path is async (submit_answers_and_match, process_answers_into_profile,
//    submit_followup_answers_and_match). run_internal_matching itself stays sync — the
//    deterministic engine has no async dependency.
//  - Python tests monkeypatch module attributes (rectification.deterministic_top_candidates,
//    rectification.store_candidate_times, rectification.process_user_answers,
//    rectification.run_internal_matching). TS exports are const bindings, so each patched
//    dependency goes through a live `let` seam with a set_* function (pattern:
//    scoring_common.set_exact_time_weight_multiplier).

import { event_date_problem } from "./event_intake";
import { initial_intake_questions } from "./intake_questions";
import {
  append_log,
  candidate_list_from_times,
  normalize_event,
  normalized_text,
  store_candidate_times,
} from "./json_store";
import { process_user_answers } from "./openai_service";
import {
  DEFAULT_TOP_ASTRO_CANDIDATES,
  DETERMINISTIC_GENERATION_METHOD,
  deterministic_top_candidates,
  is_multiday_search,
} from "./scoring";
import { nowISOSeconds } from "./datetime";
import { _truthy } from "./pyutil";

// Single-sourced from lib/scoring (was duplicated here).
import { REFINEMENT_RESULTS_PER_PRIOR_TIME } from "./scoring";

// ---------------------------------------------------------------------------
// Test seams (Python module-attribute monkeypatching → live let + setter)
// ---------------------------------------------------------------------------

type ProcessUserAnswers = (
  user_profile: Record<string, any>,
  questions: Array<Record<string, any>>,
  answers: Record<string, string>,
  extra_info: string,
  run_data: Record<string, any> | null,
) =>
  | Promise<[Record<string, any> | null, string | null, string | null]>
  | [Record<string, any> | null, string | null, string | null];

let _process_user_answers: ProcessUserAnswers = process_user_answers;
let _deterministic_top_candidates: typeof deterministic_top_candidates = deterministic_top_candidates;
let _store_candidate_times: typeof store_candidate_times = store_candidate_times;
let _run_internal_matching: ((user_profile: Record<string, any>, run_data: Record<string, any>) => void) | null = null;

export function set_process_user_answers(fn: ProcessUserAnswers | null): void {
  _process_user_answers = fn ?? process_user_answers;
}

export function set_deterministic_top_candidates(fn: typeof deterministic_top_candidates | null): void {
  _deterministic_top_candidates = fn ?? deterministic_top_candidates;
}

export function set_store_candidate_times(fn: typeof store_candidate_times | null): void {
  _store_candidate_times = fn ?? store_candidate_times;
}

/** Patch run_internal_matching itself (regenerate_internal_candidates calls through this seam). */
export function set_run_internal_matching(
  fn: ((user_profile: Record<string, any>, run_data: Record<string, any>) => void) | null,
): void {
  _run_internal_matching = fn;
}

// ---------------------------------------------------------------------------

// _truthy is the shared Python-truthiness helper from ./pyutil.

function _normalized_text(value: any): string {
  return String(_truthy(value) ? value : "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
}

function _event_key(event: Record<string, any>): string {
  return [
    _normalized_text(event["label"]),
    String(event["start_date"] ?? "").trim(),
    _normalized_text(event["category"]),
  ].join("\u0000");
}

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function ensure_questions(user_profile: Record<string, any>, run_data: Record<string, any>): void {
  if (_truthy(run_data["current_questions"])) {
    return;
  }
  run_data["current_questions"] = initial_intake_questions(user_profile);
  run_data["stage"] = "answering_questions";
  append_log(run_data, "Prepared intake questions.");
}

export async function submit_answers_and_match(
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
  answers: Record<string, string>,
  extra_info: string,
): Promise<void> {
  const questions = run_data["current_questions"] ?? [];
  append_log(run_data, "Sending answers to OpenAI for event extraction.");
  if (!(await process_answers_into_profile(user_profile, run_data, questions, answers, extra_info))) {
    return;
  }
  run_internal_matching(user_profile, run_data);
}

export async function process_answers_into_profile(
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
  questions: Array<Record<string, any>>,
  answers: Record<string, string>,
  extra_info: string,
): Promise<boolean> {
  const [extracted, , error] = await _process_user_answers(user_profile, questions, answers, extra_info, run_data);
  if (_truthy(error)) {
    append_log(run_data, error!);
    return false;
  }

  const existing_events = new Set(
    (user_profile["events"] ?? []).filter(_is_plain_object).map((item: Record<string, any>) => _event_key(item)),
  );

  const birth_date = (_truthy(user_profile["birth"]) ? user_profile["birth"] : {})["date"];
  const skipped_bad_dates: Array<Record<string, any>> = [];
  let events_added = false;
  const edited_events: string[] = [];
  for (let event of (extracted ?? {})["events"] ?? []) {
    event = normalize_event(event);
    // EDITING: the model marks an update by naming the existing event it replaces. Pop the
    // marker so it never persists onto the stored event.
    const replaces = normalized_text(event["replaces_label"]);
    delete event["replaces_label"];
    // AI extraction can hallucinate or mis-read a year; an event before birth or in the
    // future would silently poison the dasha scoring, so reject it here with a log line.
    const problem = event_date_problem(_truthy(event["start_date"]) ? event["start_date"] : "", {
      birth_date: birth_date,
      end_date: event["end_date"],
    });
    if (_truthy(problem)) {
      skipped_bad_dates.push({ label: event["label"] ?? "", problem: problem });
      continue;
    }
    if (replaces) {
      const list: Array<Record<string, any>> = Array.isArray(user_profile["events"]) ? user_profile["events"] : [];
      const idx = list.findIndex(
        (existing) => _is_plain_object(existing) && normalized_text(existing["label"]) === replaces,
      );
      if (idx >= 0) {
        existing_events.delete(_event_key(list[idx]));
        list[idx] = event;
        existing_events.add(_event_key(event));
        edited_events.push(String(event["label"] ?? replaces));
        events_added = true; // an edit is an evidence change exactly like an add
        continue;
      }
      // No match → fall through and treat it as a new event rather than dropping it.
    }
    const key = _event_key(event);
    if (!existing_events.has(key)) {
      if (!Array.isArray(user_profile["events"])) user_profile["events"] = [];
      user_profile["events"].push(event);
      existing_events.add(key);
      events_added = true;
    }
  }
  if (edited_events.length) {
    append_log(run_data, `Updated ${edited_events.length} existing event(s): ${edited_events.join("; ")}`);
  }
  if (events_added) {
    // New evidence: restart the refinement loop so the next run is a fresh full scan —
    // refining around winners chosen WITHOUT this event could lock in a wrong region.
    user_profile["loop_count"] = 0;
  }
  if (skipped_bad_dates.length) {
    const labels = skipped_bad_dates
      .map((item) => `${_truthy(item["label"]) ? item["label"] : "unlabelled"}: ${item["problem"]}`)
      .join("; ");
    append_log(run_data, `Skipped ${skipped_bad_dates.length} extracted event(s) with impossible dates — ${labels}`);
  }

  // The facts feature has been removed: any `facts`/`info` the model returns is ignored;
  // only dated events are extracted.
  delete user_profile["info"];
  run_data["current_questions"] = [];
  run_data["stage"] = "ready_internal_matching";
  run_data["updated_at"] = nowISOSeconds();
  append_log(run_data, "OpenAI converted answers into durable events.");
  return true;
}

export function run_internal_matching(
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
  options: { advance_loop?: boolean } = {},
): void {
  // `advance_loop: false` runs the full matching pass WITHOUT advancing loop_count. Reserved for
  // re-scans that merely RESTORE state (the load-time auto re-scan after a reload — candidate
  // scores are ephemeral and recomputed on open): reopening the app is not new evidence, so it
  // must not tighten the refinement region. Because that auto re-scan reaches this function
  // through the worker (App.tsx dispatches the op with no params), app_service signals it with a
  // one-shot `suppress_loop_advance` flag on run_data (in-memory only — clean_run_data_for_save
  // never persists it), consumed here by the first matching pass after load. An explicit
  // options.advance_loop always wins; every other caller keeps the default (advance) behaviour.
  const suppress_flag = run_data["suppress_loop_advance"] === true;
  delete run_data["suppress_loop_advance"];
  const advance_loop = options.advance_loop ?? !suppress_flag;
  // Legacy profiles can hold events saved before date validation existed. The scorer skips
  // impossible dates (pre-birth / future) silently per candidate; tell the user ONCE here so
  // a missing event reads as "bad date", not "the engine ignored my marriage".
  const birth_date = (_truthy(user_profile["birth"]) ? user_profile["birth"] : {})["date"];
  const impossible: string[] = [];
  for (const event of user_profile["events"] ?? []) {
    if (!_is_plain_object(event) || !_truthy(event["start_date"])) {
      continue;
    }
    const problem = event_date_problem(event["start_date"], { birth_date: birth_date, end_date: event["end_date"] });
    if (_truthy(problem)) {
      impossible.push(`“${_truthy(event["label"]) ? event["label"] : event["start_date"]}” (${problem})`);
    }
  }
  if (impossible.length) {
    append_log(
      run_data,
      `⚠ ${impossible.length} event(s) have impossible dates and are EXCLUDED from scoring: ` +
        impossible.join("; "),
    );
  }
  const active_count = candidate_list_from_times(user_profile).length;
  const limit =
    Math.trunc(Number(user_profile["loop_count"] ?? 0)) > 0 && active_count
      ? active_count * REFINEMENT_RESULTS_PER_PRIOR_TIME
      : DEFAULT_TOP_ASTRO_CANDIDATES;
  const top_times = _deterministic_top_candidates(user_profile, user_profile["events"] ?? [], limit, true);
  for (const item of top_times) {
    if (!("rejected" in item)) {
      item["rejected"] = false;
    }
  }
  _store_candidate_times(user_profile, top_times);
  // A multi-day window is an exploration seed-scan across many days; the per-winner refinement
  // loop (which assumes a single ~2h slice and a time-of-day candidate model) does not apply until
  // the user COMMITS a day+rising-sign and the window narrows to a single day. So keep loop_count
  // at 0 while multi-day — every pass stays a fresh coarse dated sweep.
  if (advance_loop && top_times.length && !is_multiday_search(user_profile["search"])) {
    // A pass completed and produced winners: the NEXT matching run is a refinement pass
    // around them — selected_candidate_tuples switches to per-winner windows and
    // adaptive_min_gap_seconds / refinement_window_radius_seconds tighten with each loop.
    // (This increment is what makes the loop-aware tiers reachable at all; loop_count is
    // reset to 0 whenever the evidence or the search window changes.)
    user_profile["loop_count"] = Math.trunc(Number(user_profile["loop_count"] ?? 0)) + 1;
  }
  run_data["stage"] = "showing_internal_times";
  run_data["updated_at"] = nowISOSeconds();
  append_log(
    run_data,
    `Internal matcher saved ${top_times.length} candidate time(s) ` +
      `(loop ${Math.trunc(Number(user_profile["loop_count"] ?? 0))}; the next run refines around them).`,
  );
}

export function candidate_times_need_regeneration(user_profile: Record<string, any>): boolean {
  const candidates = candidate_list_from_times(user_profile, true);
  return (
    candidates.length > 0 &&
    candidates.some((item) => item["generation_method"] !== DETERMINISTIC_GENERATION_METHOD)
  );
}

export function regenerate_internal_candidates(
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
): void {
  if (candidate_times_need_regeneration(user_profile)) {
    user_profile["times"] = {};
    user_profile["_times"] = { schema_version: 1, user_slug: user_profile["slug"] ?? "user", times: {} };
    user_profile["loop_count"] = 0;
    append_log(run_data, "Cleared stale candidate times before regeneration.");
  }
  append_log(run_data, "Regenerating internal candidates with the deterministic chart engine.");
  (_run_internal_matching ?? run_internal_matching)(user_profile, run_data);
}

export function reject_candidate_time(
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
  time_label: string,
): void {
  let marked = false;
  if (_is_plain_object(user_profile["times"]) && time_label in user_profile["times"]) {
    user_profile["times"][time_label]["rejected"] = true;
    marked = true;
  }
  const times_state = _is_plain_object(user_profile["_times"]) ? user_profile["_times"] : {};
  const full_times = _is_plain_object(times_state["times"]) ? times_state["times"] : {};
  if (time_label in full_times) {
    full_times[time_label]["rejected"] = true;
    marked = true;
  }
  append_log(
    run_data,
    marked ? `Marked candidate time ${time_label} as rejected.` : `Candidate time ${time_label} was not present.`,
  );
}

export async function submit_followup_answers_and_match(
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
  answers: Record<string, string>,
  extra_info: string,
): Promise<void> {
  // Preserve which times the user has already rejected before the rescan wipes them.
  const rejected_times = new Set(
    candidate_list_from_times(user_profile, true)
      .filter((item) => _truthy(item["rejected"]))
      .map((item) => item["time"]),
  );
  append_log(
    run_data,
    `Adding new events. Running a full recalculation across the entire search range. ${rejected_times.size} previously rejected time(s) will stay rejected.`,
  );
  const questions = run_data["current_questions"] ?? [];
  if (await process_answers_into_profile(user_profile, run_data, questions, answers, extra_info)) {
    // Keep loop_count at 0 so run_internal_matching does a full scan (not clustered around prior times).
    user_profile["loop_count"] = 0;
    run_internal_matching(user_profile, run_data);
    for (const time_label of rejected_times) {
      reject_candidate_time(user_profile, run_data, time_label as string);
    }
  }
}

/**
 * Wipe all derived candidate progress (times, reviews, scores, analyses,
 * follow-up questions, scoring forms, loop count) while keeping the durable
 * events/birth/search gathered during intake, then restart matching.
 */
export function clear_candidate_progress(user_profile: Record<string, any>, run_data: Record<string, any>): void {
  user_profile["times"] = {};
  user_profile["_times"] = { schema_version: 1, user_slug: user_profile["slug"] ?? "user", times: {} };
  user_profile["loop_count"] = 0;
  for (const key of ["current_questions", "_chart_analysis", "scoring_form", "personality_matches"]) {
    delete run_data[key];
  }
  run_data["current_questions"] = [];
  run_data["stage"] = "ready_internal_matching";
  run_data["updated_at"] = nowISOSeconds();
  append_log(run_data, "Cleared all candidate times, reviews and scores. Restarting matching from the saved events.");
}

/**
 * COMPLETE reset: keep ONLY the user's birth details (date of birth + place) and their dated
 * events. Everything else is wiped — appearance traits, the trait chat + its OpenAI conversation,
 * the rising-sign likelihood, life periods, self-description answers, and all computed
 * candidate times / reviews / scores — and the search window is restored to the full intake range
 * (undoing any rising-sign narrowing).
 */
export function full_reset_keep_birth_events(user_profile: Record<string, any>, run_data: Record<string, any>): void {
  const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
  // Everything that is NOT birth or events is cleared back to empty.
  user_profile["traits"] = {};
  user_profile["rejected_traits"] = {};
  user_profile["trait_influence"] = {};
  user_profile["excluded_rising_signs"] = [];
  user_profile["rising_sign_locked"] = false;
  user_profile["zoom_stack"] = [];
  user_profile["life_periods"] = {};
  // Split trait-chat state (appearance + personality), plus the legacy single-chat fields.
  user_profile["appearance_chat"] = [];
  user_profile["appearance_conversation_id"] = null;
  user_profile["personality_chat"] = [];
  user_profile["personality_conversation_id"] = null;
  user_profile["trait_chat"] = [];
  user_profile["trait_chat_choices"] = [];
  user_profile["trait_conversation_id"] = null;
  delete user_profile["trait_chat_collapsed"];
  user_profile["scoring_answers"] = [];
  user_profile["scoring_notes"] = [];
  delete user_profile["rising_sign_likelihood"];
  // Marked-times comparison state — a shortlist of computed candidate seconds, so it resets
  // with the rest of the computed progress (plus its chat + display-only AI assessments).
  user_profile["marked_times"] = [];
  user_profile["compare_eliminated"] = [];
  user_profile["compare_assessments"] = {};
  user_profile["compare_ranks"] = {};
  user_profile["compare_chat"] = [];
  user_profile["compare_conversation_id"] = null;
  user_profile["compare_final_key"] = null;
  delete user_profile["compare_chat_keys"];
  // Computed candidate progress.
  user_profile["times"] = {};
  user_profile["_times"] = { schema_version: 1, user_slug: user_profile["slug"] ?? "user", times: {} };
  user_profile["loop_count"] = 0;
  // Restore the original (un-narrowed) search window from the saved birth range.
  const search = { ...(_truthy(user_profile["search"]) ? user_profile["search"] : {}) };
  search["start"] = _truthy(birth["range_start"]) ? birth["range_start"] : "00:00:00";
  search["end"] = _truthy(birth["range_end"]) ? birth["range_end"] : "23:59:59";
  search["start_date"] = birth["date"];
  search["end_date"] = _truthy(birth["range_end_date"]) ? birth["range_end_date"] : birth["date"];
  delete search["step_seconds"];
  user_profile["search"] = search;
  // Reset run state.
  for (const key of ["current_questions", "_chart_analysis", "scoring_form", "personality_matches"]) {
    delete run_data[key];
  }
  run_data["current_questions"] = [];
  run_data["stage"] = "ready_internal_matching";
  run_data["updated_at"] = nowISOSeconds();
  append_log(
    run_data,
    "Full reset: kept birth details and events; cleared traits, chat, life periods and all computed times.",
  );
}

export function candidate_has_ai_review(candidate: Record<string, any>): boolean {
  if (
    _truthy(candidate["personality_paragraph"]) ||
    _truthy(candidate["ai_summary"]) ||
    _truthy(candidate["chart_analysis"])
  ) {
    return true;
  }
  return Boolean(
    _truthy(candidate["milestone_periods"]) ||
      _truthy(candidate["evidence_for"]) ||
      _truthy(candidate["other_notable_things"]),
  );
}

/** Compatibility wrapper for older UI/tests. */
export function run_iteration(user_profile: Record<string, any>, run_data: Record<string, any>): Record<string, any> {
  const top_times = _deterministic_top_candidates(
    user_profile,
    user_profile["events"] ?? [],
    DEFAULT_TOP_ASTRO_CANDIDATES,
    true,
  );
  _store_candidate_times(user_profile, top_times);
  run_data["stage"] = "showing_internal_times";
  return run_data;
}
